import React from 'react';
import Humanize from 'humanize-plus';
import { LomavisShortenedNumber } from '../../utils/numbers/LomavisShortenedNumber';
import { LomavisOverlayToolTip1 } from '../../overlays/lomavisOverlayToolTip1/lomavisOverlayToolTip1';
import { translateComponent } from '../../../../utils/tolgee/translateComponent';

type Props = {
  before: number;
  current: number;
};

/**
 * A locale-aware, shortened number display component.
 * It optionally abbreviates large numbers (e.g., 12.3k) and shows a tooltip with the full value.
 */
export const LomavisGrowthBadge: React.FC<Props> = ({ before, current }) => {
  const t = translateComponent('LomavisGrowthBadge');

  const delta = current - before;
  const isPositive = delta >= 0;

  const growthPercentRaw = before === 0 ? 100 : (delta / before) * 100;
  let displayPercent = Math.max(1, Math.round(Math.abs(growthPercentRaw)));
  let absoluteChange = Math.round(Math.abs(delta));

  if (before === 0 && current === 0) {
    displayPercent = 0;
    absoluteChange = 0;
  }

  const colorClass = isPositive
    ? 'text-success bg-success fw-bold bg-opacity-10'
    : 'text-danger bg-danger fw-bold bg-opacity-10';

  const arrowClass = isPositive
    ? 'fa-arrow-up text-success'
    : 'fa-arrow-down text-danger';

  const tooltipText = `${String(t('previous'))}: ${String(
    Humanize.formatNumber(before)
  )}\n${String(t('current'))}: ${String(
    Humanize.formatNumber(current)
  )}\n${String(t('change'))}: ${isPositive ? '+' : '-'}${String(
    Humanize.formatNumber(absoluteChange)
  )} (${displayPercent}%)`;

  return (
    <LomavisOverlayToolTip1 text={tooltipText} fromCard={true}>
      <div
        className={`d-inline-flex align-items-center gap-2 px-3 py-2 badge ${colorClass} no-a11y-check`}
      >
        <i className={`fa-duotone fs-5 ${arrowClass}`} />
        <span className="fw-semibold fs-5 ignore-a11y">
          <LomavisShortenedNumber
            value={displayPercent}
          ></LomavisShortenedNumber>{' '}
          %
        </span>
        <span className="fw-semibold fs-5 ignore-a11y">|</span>
        <span className="fw-semibold fs-5 ignore-a11y">
          <LomavisShortenedNumber
            value={absoluteChange}
          ></LomavisShortenedNumber>
        </span>
      </div>
    </LomavisOverlayToolTip1>
  );
};
